// GeneratedArticleModal.js
import React from "react";
import { BlockStack, Modal, Text } from "@shopify/polaris";
import DOMPurify from "dompurify";
import { useNavigate } from "react-router-dom";

function GeneratedArticleModal({ open, onClose, article, shopOrigin }) {
  const navigate = useNavigate();

  // console.log('GeneratedArticleModal - article prop:', article);
  
  if (!article) {
    return (
      <Modal open={open} onClose={onClose} title="Generated Article Preview">
        <Modal.Section>
          <Text>No content available.</Text>
        </Modal.Section>
      </Modal>
    );
  }

  // Extract the actual article data from the nested structure
  const actualArticle = article[0] || article;

  // Check if actualArticle has the required properties
  if (!actualArticle.title && !actualArticle.content) {
    return (
      <Modal open={open} onClose={onClose} title="Generated Article Preview">
        <Modal.Section>
          <Text>Article data is malformed.</Text>
        </Modal.Section>
      </Modal>
    );
  }

  // Custom styles for modal content
  const modalContentStyles = `
    .Polaris-Modal-Dialog__Modal {
      max-width: 65% !important;
    }
    .generated-article-modal-content {
      line-height: 1.5;
      font-size: 14px;
      color: #2c2c2c;
    }
    .generated-article-modal-content h1{
      font-size: 28px;
      font-weight: 700;
      margin: 0 0 15px 0;
    }
    .generated-article-modal-content h2 {
      font-weight: 700;
      font-size: 1.45em;
      border-radius: 4px;
      margin-bottom: 12px
    }
    .generated-article-modal-content h3 {
      font-weight: 600;
      font-size: 1.25em;
      margin: 0 0 12px 0;
    }
    .generated-article-modal-content p {
      margin: 0 0 15px 0;
    }
    .generated-article-modal-content ul {
      margin: 0px 0 0px 15px;
      padding-left: 20px;
      list-style-type: disc;
    }
    .generated-article-modal-content li {
      margin-bottom: 8px;
    }
    .generated-article-modal-content strong {
      font-weight: 700;
    }
    .generated-article-modal-content em {
      font-style: italic;
    }
    .generated-article-modal-content br.newline-block {
      display: block;
      margin-bottom: 12px;
    }
    .Polaris-Modal-Header__Title {
      font-size: 17px !important;
    }
    .generated-article-modal-content u,
    .generated-article-modal-content .underline {
      text-decoration: underline;
    }
    .generated-article-modal-content s,
    .generated-article-modal-content strike,
    .generated-article-modal-content .strikethrough {
      text-decoration: line-through;
    }
    .generated-article-modal-content mark {
      background-color: #ffe066;
    }
  `;

  // Sanitize the content
  const sanitizedContent = DOMPurify.sanitize(actualArticle.content, {
    ALLOWED_TAGS: [
      "p", "br", "strong", "em", "ul", "li", "a", "h1", "h2", "h3",
      "span", "img", "div", "table", "u", "s", "strike", "mark"
    ],
    ALLOWED_ATTR: [
      "href", "src", "alt", "width", "height", "style", "data-contrast", "class"
    ]
  });

  const formattedContent = sanitizedContent.replace(/\n\n\n\n/g, '<br class="newline-block">');

  const handleSaveAsDraft = () => {
    // Navigate directly to edit-blogs page with article data in state
    // Format the data to match what EditBlogs expects
    navigate(`/edit-blogs?shop=${encodeURIComponent(shopOrigin)}`, {
      state: {
        article: {
          articletitle: actualArticle.title || 'Untitled',
          title: actualArticle.title || 'Untitled',
          articlebody: actualArticle.content || '',
          content: actualArticle.content || '',
          descriptive: actualArticle.descriptive || '',
          readtime: actualArticle.readtime || '',
          published: false,
          keywords: [],
          tags: [],
        }
      }
    });
  };

  return (
    <>
      <style>{modalContentStyles}</style>
      <Modal
        open={open}
        onClose={onClose}
        title="Generated Article Preview"
        primaryAction={{
          content: 'Go to Editor',
          onAction: handleSaveAsDraft,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: onClose,
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {/* Title */}
            <Text variant="headingLg" as="h2">
              {actualArticle.title || 'Untitled'}
            </Text>

            {/* Description */}
            {actualArticle.descriptive && (
              <Text variant="bodyMd" tone="subdued">
                {actualArticle.descriptive}
              </Text>
            )}

            {/* Read Time */}
            {actualArticle.readtime && (
              <Text variant="bodySm">
                {actualArticle.readtime}
              </Text>
            )}

            {/* Content with HTML */}
            <div
              className="generated-article-modal-content"
              style={{
                border: '1px solid #e1e3e5',
                borderRadius: '8px',
                padding: '16px',
                maxHeight: '500px',
                overflowY: 'auto',
              }}
              dangerouslySetInnerHTML={{ __html: formattedContent }}
            />
          </BlockStack>
        </Modal.Section>
      </Modal>
    </>
  );
}

export default GeneratedArticleModal;
